import React from "react";
import { Box, Typography, Alert } from "@mui/material";
import { Info } from "@mui/icons-material";
import { ProcessStripeDepositRes } from "src/store/features/api/generated";
import BankingRow from "src/components/banking/BankingRow";

interface StripePaymentPageProps {
  stripeData: ProcessStripeDepositRes;
  clientDetails: { customerId: string; name: string };
}

export default function StripePaymentPage({ stripeData, clientDetails }: StripePaymentPageProps) {
  const { aba_financial_address } = stripeData;

  if (!aba_financial_address) {
    return (
      <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: "auto" }}>
          <Typography variant="h6" gutterBottom>
            Bank Transfer Details Unavailable
          </Typography>
          <Typography variant="body2">
            Bank transfer details are not available at the moment. Please try again later.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const formatAddress = (address: any) => {
    if (!address) return "Address not available";

    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.state,
      address.postal_code,
      address.country,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(", ") : "Address not available";
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Banking Rows - matching MainBankDetails structure exactly */}
      <Box>
        <BankingRow
          placeholder="Account Holder Name"
          content={aba_financial_address.account_holder_name || "Not available"}
          isCopy={false}
          copyToClipboard={() => {}}
        />
      </Box>

      <Box>
        <BankingRow
          placeholder="Account Number"
          content={aba_financial_address.account_number || "Not available"}
          isCopy={true}
          copyToClipboard={() => copyToClipboard(aba_financial_address.account_number)}
        />
      </Box>

      <Box>
        <BankingRow
          placeholder="Routing Number"
          content={aba_financial_address.routing_number || "Not available"}
          isCopy={true}
          copyToClipboard={() => copyToClipboard(aba_financial_address.routing_number)}
        />
      </Box>

      <Box>
        <BankingRow
          placeholder="Bank Name"
          content={aba_financial_address.bank_name || "Not available"}
          isCopy={false}
          copyToClipboard={() => {}}
        />
      </Box>

      <Box>
        <BankingRow
          placeholder="Account Type"
          content={aba_financial_address.account_type || "Not available"}
          isCopy={false}
          copyToClipboard={() => {}}
        />
      </Box>

      <Box>
        <BankingRow
          placeholder="Account Holder Address"
          content={formatAddress(aba_financial_address.account_holder_address)}
          isCopy={false}
          copyToClipboard={() => {}}
        />
      </Box>

      <Box>
        <BankingRow
          placeholder="Bank Address"
          content={formatAddress(aba_financial_address.bank_address)}
          isCopy={false}
          copyToClipboard={() => {}}
        />
      </Box>

      {/* Important Notice */}
      <Box sx={{ mt: 3 }}>
        <Alert
          severity="info"
          icon={<Info />}
          sx={{
            borderRadius: 1,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
            Important Transfer Instructions
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Reference Number:</strong> Always include your customer ID{" "}
            <strong>{clientDetails.customerId}</strong> in the transfer reference or description.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This ensures your deposit is credited to the correct account. Transfers without proper
            reference may be delayed or returned.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
}
